import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ShieldCheck, AlertCircle } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";
import { adminApi } from "@/lib/auth";
import { toast } from "sonner";

/**
 * Onboarding modal shown to instructors the first time they enter the app.
 * Asks for their master profile signature (used by every certificate the app
 * generates for the courses they teach).
 *
 * Props:
 *  - open: boolean — controls visibility
 *  - onSaved: () => void — called after a successful save
 */
export default function InstructorSignatureOnboarding({ open, onSaved }) {
  const [showPad, setShowPad] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async (signatureBase64) => {
    setSaving(true);
    try {
      await adminApi.post("/instructor/me/signature", {
        signature_base64: signatureBase64,
      });
      toast.success("Firma registrada correctamente.");
      setShowPad(false);
      onSaved?.();
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "No se pudo guardar la firma."
      );
    } finally {
      setSaving(false);
    }
  };

  if (showPad) {
    return (
      <SignaturePad
        courseName="Firma del instructor"
        studentName="Tu firma se reutilizará en cada certificado emitido."
        onCancel={() => setShowPad(false)}
        onSave={handleSave}
        submitting={saving}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        /* Cannot dismiss: signature is mandatory the first time */
      }}
    >
      <DialogContent
        className="bg-white border border-zinc-200 max-w-lg"
        data-testid="instructor-signature-onboarding"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-3">
            <ShieldCheck className="h-6 w-6 text-zinc-900" />
          </div>
          <DialogTitle className="heading-font text-2xl sm:text-3xl font-extrabold text-zinc-950 text-center">
            Registra tu firma
          </DialogTitle>
          <DialogDescription className="text-zinc-600 text-center leading-relaxed pt-2">
            Antes de empezar, necesitamos tu firma. La utilizaremos
            automáticamente en los certificados y documentación que se generen
            de los cursos que impartas.
          </DialogDescription>
        </DialogHeader>

        <div
          className="bg-zinc-50 border border-zinc-200 rounded-md p-4 text-sm text-zinc-700 leading-relaxed flex gap-3"
          data-testid="signature-privacy-notice"
        >
          <AlertCircle className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
          <p>
            Tu firma <strong>solo</strong> se utilizará para las gestiones
            necesarias relacionadas con los cursos de <strong>Desfiemer</strong>{" "}
            (certificados, control de firmas y documentación oficial). No se
            cederá a terceros ni se usará para otros fines.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setShowPad(true)}
          className="mt-2 w-full h-12 inline-flex items-center justify-center bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold uppercase tracking-wide transition-colors"
          data-testid="open-signature-pad-button"
        >
          Firmar ahora
        </button>
      </DialogContent>
    </Dialog>
  );
}
